import { getConfig } from '../config/appConfig';
import { clearAuthSession } from './authTokenStore';

export const LAST_ACTIVITY_KEY = 'candor_last_activity';
export const LOGOUT_REASON_KEY = 'candor_logout_reason';
export const SESSION_REVOKE_KEY = 'candor_session_revoked';
export const TEST_TIMEOUT_FLAG = 'candor_test_timeout';

const ACTIVITY_THROTTLE_MS = 2000;
const MIN_WARNING_MS = 30 * 1000;
const MAX_WARNING_MS = 2 * 60 * 1000;
const DEFAULT_WARNING_MS = 60 * 1000;

let lastTouchAt = 0;

/** All client auth keys cleared on logout / 401 */
export function clearAllAuthStorage() {
  clearAuthSession();
  const tokenKey = getConfig('security.tokenStorageKey', 'dashboard_token');
  [
    tokenKey,
    'candor_token',
    'dashboard_token',
    'candor_user',
    'candor_session_token',
    'candor_session_user',
    'pending_auth_code',
    'candor_cookie_session',
    LAST_ACTIVITY_KEY,
  ].forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(key);
    } catch (_) {
      /* ignore */
    }
  });
}

export function setLogoutReason(reason) {
  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch (_) {
    /* ignore */
  }
}

export function consumeLogoutReason() {
  try {
    const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return reason;
  } catch (_) {
    return null;
  }
}

export function isTestTimeoutMode() {
  if (typeof window === 'undefined') return false;
  if (sessionStorage.getItem(TEST_TIMEOUT_FLAG) === 'true') return true;
  const haystack = `${window.location.search}${window.location.hash}`;
  return haystack.includes('test_timeout=true');
}

export function enableTestTimeoutMode() {
  try {
    sessionStorage.setItem(TEST_TIMEOUT_FLAG, 'true');
  } catch (_) {
    /* ignore */
  }
}

export function disableTestTimeoutMode() {
  try {
    sessionStorage.removeItem(TEST_TIMEOUT_FLAG);
  } catch (_) {
    /* ignore */
  }
}

export function getSessionTimeoutMs() {
  if (isTestTimeoutMode()) return 15 * 1000;
  const configured = getConfig('security.sessionTimeout', 3600000);
  const n = Number(configured);
  return Number.isFinite(n) && n > 0 ? n : 3600000;
}

export function getWarningDurationMs(sessionTimeoutMs = getSessionTimeoutMs()) {
  if (isTestTimeoutMode()) return 5 * 1000;
  return Math.min(
    MAX_WARNING_MS,
    Math.max(MIN_WARNING_MS, Math.min(DEFAULT_WARNING_MS, Math.floor(sessionTimeoutMs / 2)))
  );
}

export function getLastActivityMs() {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (_) {
    return null;
  }
}

/** Record user/API activity (throttled). Returns true if timestamp was updated. */
export function touchActivity(force = false) {
  const now = Date.now();
  if (!force && now - lastTouchAt < ACTIVITY_THROTTLE_MS) return false;
  lastTouchAt = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch (_) {
    return false;
  }
  return true;
}

export function msSinceLastActivity() {
  const last = getLastActivityMs();
  if (last === null) return 0;
  return Math.max(0, Date.now() - last);
}

export function isIdleExpired(sessionTimeoutMs = getSessionTimeoutMs()) {
  const last = getLastActivityMs();
  if (last === null) return false;
  return Date.now() - last >= sessionTimeoutMs;
}

export function msUntilIdleWarning(sessionTimeoutMs = getSessionTimeoutMs()) {
  const warningMs = getWarningDurationMs(sessionTimeoutMs);
  const idleBudget = sessionTimeoutMs - warningMs;
  const elapsed = msSinceLastActivity();
  return Math.max(0, idleBudget - elapsed);
}

export function msUntilIdleExpiry(sessionTimeoutMs = getSessionTimeoutMs()) {
  const elapsed = msSinceLastActivity();
  return Math.max(0, sessionTimeoutMs - elapsed);
}

export function subscribeSessionSync(onActivity, onForceLogout) {
  if (typeof window === 'undefined') return () => {};

  const onStorage = (event) => {
    if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
      lastTouchAt = Number(event.newValue) || Date.now();
      onActivity?.();
      return;
    }
    if (event.key === SESSION_REVOKE_KEY && event.newValue) {
      const reason = String(event.newValue).split(':')[0] || 'idle';
      onForceLogout?.(reason);
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      onActivity?.({ checkExpiry: true });
    }
  };

  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/** Signal other tabs to log out (idle or unauthorized). */
export function broadcastLogout(reason) {
  setLogoutReason(reason);
  try {
    localStorage.setItem(SESSION_REVOKE_KEY, `${reason}:${Date.now()}`);
  } catch (_) {
    /* ignore */
  }
}
